import PropTypes from 'prop-types';
import STEP2_COPY from './copy';
import { LigneCalcul, Sep, BlocResultat, StatusCard, BadgeSource } from './ui';
import { fmt } from './formatters';

function CarteIncompatibleMicro({ metier, incompatibilite }) {
  const { ca_annuel, seuil, depassement } = incompatibilite;
  const fmtE = (n) => n.toLocaleString('fr-FR') + ' €';
  const { micro } = STEP2_COPY.cards;
  return (
    <div className="bg-white rounded-xl shadow-card border-4 border-red-300 p-6 flex flex-col h-full">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-2xl">📱</span>
        <h3 className="text-xl font-bold text-gray-700">{micro.title}</h3>
      </div>
      <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />

      <div className="mt-4 bg-red-50 border border-red-300 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9v4a1 1 0 102 0V9a1 1 0 10-2 0zm0-4a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
          </svg>
          <p className="text-sm font-bold text-red-800">{micro.incompatibleTitle}</p>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-red-700">Votre CA annuel :</span>
          <span className="font-semibold text-red-900 text-right">{fmtE(ca_annuel)}/an</span>
          <span className="text-red-700">Seuil franchise TVA :</span>
          <span className="font-semibold text-red-900 text-right">{fmtE(seuil)}/an</span>
          <span className="text-red-700">Dépassement :</span>
          <span className="font-semibold text-red-900 text-right">+ {fmtE(depassement)}/an</span>
        </div>

        <p className="text-xs text-red-700 border-t border-red-200 pt-3">
          {micro.incompatibleBody}
          <br />
          {micro.incompatibleLinkPrefix}
          <a
            href={micro.incompatibleLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium hover:text-red-900"
          >
            {micro.incompatibleLinkLabel}
          </a>
        </p>
        <p className="text-xs text-red-700 font-medium">{micro.incompatibleAdvice}</p>
      </div>

      {micro.seuilsNote && (
        <p className="mt-3 text-xs text-gray-500 italic px-1">{micro.seuilsNote}</p>
      )}
    </div>
  );
}

CarteIncompatibleMicro.propTypes = {
  metier: PropTypes.string.isRequired,
  incompatibilite: PropTypes.shape({
    ca_annuel: PropTypes.number.isRequired,
    seuil: PropTypes.number.isRequired,
    depassement: PropTypes.number.isRequired,
  }).isRequired,
};

export function CarteCAE({ resultat, metier, caHT, isTTC, caTTC }) {
  if (!resultat) return null;
  const cat = resultat.categories_charges ?? {};
  const mpST = cat.matieres_et_sous_traitance ?? 0;
  const fraisParticipation = cat.frais_participation_coop ?? 0;
  const fraisFonctionnement = cat.frais_fonctionnement ?? 0;
  const copy = STEP2_COPY.cards.cae;

  if (resultat.erreur_config) {
    return (
      <StatusCard icon="🟢" title={copy.title} borderColor={copy.borderColor} titleColor={copy.titleColor}>
        <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />
        <div className="mt-4 bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-2">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-9v4a1 1 0 102 0V9a1 1 0 10-2 0zm0-4a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-bold text-red-800">Simulation impossible</p>
            <p className="text-xs text-red-700 mt-1">{resultat.erreur_config}</p>
          </div>
        </div>
      </StatusCard>
    );
  }

  return (
    <StatusCard icon="🟢" title={copy.title} borderColor={copy.borderColor} titleColor={copy.titleColor}>
      <BadgeSource source={resultat.source} />
      <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />
      {isTTC ? (
        <>
          <LigneCalcul label="CA TTC :" valeur={fmt(caTTC)} strong />
          <LigneCalcul label="→ CA HT :" valeur={fmt(caHT)} muted indent />
        </>
      ) : (
        <LigneCalcul label="CA HT :" valeur={fmt(caHT)} strong />
      )}

      {mpST > 0 && (
        <LigneCalcul label="− Matières premières et sous-traitance :" valeur={fmt(mpST)} muted indent />
      )}
      <LigneCalcul label="= Marge brute :" valeur={fmt(resultat.marge_brute)} strong dividerTop />

      <LigneCalcul label="− Frais de participation coopérative :" valeur={fmt(fraisParticipation)} muted indent />
      {fraisFonctionnement > 0 && (
        <LigneCalcul label="− Frais de fonctionnement :" valeur={fmt(fraisFonctionnement)} muted indent />
      )}
      <LigneCalcul label="= Marge économique :" valeur={fmt(resultat.marge_economique)} strong dividerTop />

      {(() => {
        const chargesForfaitairesTotal = (resultat.details?.charges_forfaitaires ?? 0) + (resultat.details?.cotisation_fede_urscop ?? 0);
        const cpEstimes = (resultat.salaire_net_avant_pas ?? 0) - (resultat.salaire_net_sans_cp ?? resultat.salaire_net_avant_pas ?? 0);
        const avecCP = cpEstimes > 0;
        return (
          <>
            {chargesForfaitairesTotal > 0 && (
              <LigneCalcul label="− Charges forfaitaires :" valeur={fmt(chargesForfaitairesTotal)} muted indent />
            )}
            <LigneCalcul label={avecCP ? '− Cotisations patronales (y compris cotis. caisse CP) :' : '− Cotisations patronales :'} valeur={fmt(resultat.charges_patronales)} muted indent />
            <LigneCalcul label="= Salaire brut :" valeur={fmt(resultat.salaire_brut_api)} strong dividerTop />
            <LigneCalcul label="− Cotisations salariales :" valeur={fmt(resultat.cotisations_salariales)} muted indent />
            <LigneCalcul label={avecCP ? `= SALAIRE NET AVANT impôt (+ ${fmt(cpEstimes)} CP estimés) :` : '= SALAIRE NET AVANT impôt :'} valeur={fmt(resultat.salaire_net_avant_pas)} strong dividerTop />
          </>
        );
      })()}

      {resultat.frais_pro_recuperes > 0 && (
        <LigneCalcul label="+ Frais professionnels récupérés :" valeur={fmt(resultat.frais_pro_recuperes)} muted indent />
      )}
      <LigneCalcul label="= MONTANT TOTAL VERSÉ :" valeur={fmt(resultat.montant_total_verse)} strong dividerTop highlight />

      <Sep color="border-primary-300" />
      <BlocResultat label="Salaire net avant PAS" valeur={resultat.salaire_net_avant_pas} color="text-primary-600" />
      {resultat.montant_total_verse !== resultat.salaire_net_avant_pas && (
        <BlocResultat label={copy.totalLabel} valeur={resultat.montant_total_verse} color="text-green-600" sub={copy.totalSub} />
      )}

      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-green-900">{copy.protectionTitle}</p>
          <p className="text-xs text-green-700 mt-0.5">{copy.protectionSubtitle}</p>
          <p className="text-sm font-semibold text-green-900 mt-1.5">{copy.accompagnementTitle}</p>
          {copy.collectifTitle && (
            <p className="text-sm font-semibold text-green-900 mt-1.5">{copy.collectifTitle}</p>
          )}
        </div>
      </div>

      {resultat.avertissement && (
        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-xs text-orange-800">{resultat.avertissement}</p>
        </div>
      )}
    </StatusCard>
  );
}

export function CarteMicro({ resultat, metier, caTTC, isTTC, caHT }) {
  if (!resultat) return null;
  if (resultat.incompatible) {
    return <CarteIncompatibleMicro metier={metier} incompatibilite={resultat.incompatibilite} />;
  }
  const cat = resultat.categories_charges ?? {};
  const mpST = cat.matieres_et_sous_traitance ?? 0;
  const autresStructure = cat.autres_frais_structure ?? 0;
  const fraisFonctionnement = cat.frais_fonctionnement ?? 0;
  const copy = STEP2_COPY.cards.micro;

  return (
    <StatusCard icon="📱" title={copy.title} borderColor={copy.borderColor}>
      <BadgeSource source={resultat.source} />
      <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />
      {isTTC ? (
        <LigneCalcul label="CA TTC :" valeur={fmt(caTTC)} strong />
      ) : (
        <>
          <LigneCalcul label="CA HT :" valeur={fmt(caHT)} strong />
          <LigneCalcul label="→ base cotisations TTC :" valeur={fmt(caTTC)} muted indent />
        </>
      )}

      <div className="mt-2 mb-1 text-xs text-gray-400 italic px-1">{copy.chargesInfoLabel}</div>
      {mpST > 0 && (
        <LigneCalcul label="Matières premières et sous-traitance (non déductibles) :" valeur={fmt(mpST)} info indent />
      )}
      {autresStructure > 0 && (
        <LigneCalcul label="Autres frais de structure (non déductibles) :" valeur={fmt(autresStructure)} info indent />
      )}
      {fraisFonctionnement > 0 && (
        <LigneCalcul label="Frais de fonctionnement (non déductibles) :" valeur={fmt(fraisFonctionnement)} info indent />
      )}

      <LigneCalcul label="CA (base cotisations) :" valeur={fmt(caTTC)} strong dividerTop />
      <LigneCalcul
        label={`− Cotisations sociales (${resultat.taux_cotisations?.toFixed(1) ?? ''}%) :`}
        valeur={fmt(resultat.cotisations_sociales)}
        muted
        indent
      />
      <LigneCalcul label="= Revenu net avant impôt :" valeur={fmt(resultat.revenu_net_avant_pas)} strong dividerTop />

      <LigneCalcul label="− Charges de l'activité :" valeur={fmt(resultat.charges_non_deductibles)} muted indent />
      <LigneCalcul label="= MONTANT TOTAL DISPONIBLE :" valeur={fmt(resultat.revenu_reel_disponible)} strong dividerTop highlight />

      <Sep color="border-blue-300" />
      <BlocResultat label="Revenu net avant impôt" valeur={resultat.revenu_net_avant_pas} color="text-blue-600" />
      {resultat.revenu_reel_disponible !== resultat.revenu_net_avant_pas && (
        <BlocResultat label={copy.availableLabel} valeur={resultat.revenu_reel_disponible} color="text-orange-600" sub={copy.availableSub} />
      )}

      {resultat.avertissement && (
        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-xs text-orange-800">{resultat.avertissement}</p>
        </div>
      )}

      <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center gap-2">
        <span className="text-base">⚠️</span>
        <p className="text-xs font-semibold text-yellow-800">Protection sociale faible</p>
      </div>

      {copy.seuilsNote && (
        <p className="mt-3 text-xs text-gray-500 italic px-1">{copy.seuilsNote}</p>
      )}
    </StatusCard>
  );
}

export function CarteSASU({ resultat, metier, caHT, isTTC, caTTC }) {
  if (!resultat) return null;
  const cat = resultat.categories_charges ?? {};
  const mpST = cat.matieres_et_sous_traitance ?? 0;
  const autresCharges = cat.autres_frais_structure ?? 0;
  const fraisFonctionnement = cat.frais_fonctionnement ?? 0;
  const margeBrute = resultat.marge_brute ?? (caHT - mpST);
  const copy = STEP2_COPY.cards.sasu;

  return (
    <StatusCard icon="🏢" title={copy.title} borderColor={copy.borderColor}>
      <BadgeSource source={resultat.source} />
      <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />
      {isTTC ? (
        <>
          <LigneCalcul label="CA TTC :" valeur={fmt(caTTC)} strong />
          <LigneCalcul label="→ CA HT :" valeur={fmt(caHT)} muted indent />
        </>
      ) : (
        <LigneCalcul label="CA HT :" valeur={fmt(caHT)} strong />
      )}

      {mpST > 0 && (
        <LigneCalcul label="− Matières premières et sous-traitance :" valeur={fmt(mpST)} muted indent />
      )}
      <LigneCalcul label="= Marge brute :" valeur={fmt(margeBrute)} strong dividerTop />

      {autresCharges > 0 && (
        <LigneCalcul label="− Autres frais de la structure :" valeur={fmt(autresCharges)} muted indent />
      )}
      {fraisFonctionnement > 0 && (
        <LigneCalcul label="− Frais de fonctionnement :" valeur={fmt(fraisFonctionnement)} muted indent />
      )}
      <LigneCalcul label="= Marge économique :" valeur={fmt(resultat.marge_economique ?? resultat.resultat_disponible)} strong dividerTop />

      <LigneCalcul
        label={`− Cotisations patronales (${resultat.salaire_brut ? ((resultat.charges_patronales / resultat.salaire_brut) * 100).toFixed(0) : '—'}%) :`}
        valeur={fmt(resultat.charges_patronales)}
        muted
        indent
      />
      <LigneCalcul label="= Rémunération brute :" valeur={fmt(resultat.salaire_brut)} strong dividerTop />

      <LigneCalcul
        label={`− Cotisations salariales (${resultat.salaire_brut ? ((resultat.cotisations_salariales / resultat.salaire_brut) * 100).toFixed(0) : '—'}%) :`}
        valeur={fmt(resultat.cotisations_salariales)}
        muted
        indent
      />
      <LigneCalcul label="= RÉMUNÉRATION NETTE avant impôt :" valeur={fmt(resultat.salaire_net_avant_pas)} strong dividerTop highlight />

      <Sep color="border-indigo-300" />
      <BlocResultat label="Rémunération nette avant impôt" valeur={resultat.salaire_net_avant_pas} color="text-indigo-600" />

      {resultat.avertissement && (
        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-xs text-orange-800">{resultat.avertissement}</p>
        </div>
      )}
    </StatusCard>
  );
}

export function CarteEI({ resultat, metier, caHT, isTTC, caTTC }) {
  if (!resultat) return null;
  const cat = resultat.categories_charges ?? {};
  const mpST = cat.matieres_et_sous_traitance ?? 0;
  const autresCharges = cat.autres_frais_structure ?? 0;
  const fraisFonctionnement = cat.frais_fonctionnement ?? 0;
  const margeBrute = resultat.marge_brute ?? (caHT - mpST);
  const copy = STEP2_COPY.cards.ei;

  return (
    <StatusCard icon="👤" title={copy.title} borderColor={copy.borderColor}>
      <BadgeSource source={resultat.source} />
      <LigneCalcul label={`Métier\u00a0:`} valeur={metier} strong />
      {isTTC ? (
        <>
          <LigneCalcul label="CA TTC :" valeur={fmt(caTTC)} strong />
          <LigneCalcul label="→ CA HT :" valeur={fmt(caHT)} muted indent />
        </>
      ) : (
        <LigneCalcul label="CA HT :" valeur={fmt(caHT)} strong />
      )}

      {mpST > 0 && (
        <LigneCalcul label="− Matières premières et sous-traitance :" valeur={fmt(mpST)} muted indent />
      )}
      <LigneCalcul label="= Marge brute :" valeur={fmt(margeBrute)} strong dividerTop />

      {autresCharges > 0 && (
        <LigneCalcul label="− Autres frais de la structure :" valeur={fmt(autresCharges)} muted indent />
      )}
      {fraisFonctionnement > 0 && (
        <LigneCalcul label="− Frais de fonctionnement :" valeur={fmt(fraisFonctionnement)} muted indent />
      )}
      <LigneCalcul label="= Marge économique :" valeur={fmt(resultat.marge_economique ?? resultat.benefice)} strong dividerTop />

      <LigneCalcul
        label={`− Cotisations sociales (${(resultat.taux_cotisations ?? 0).toFixed(0)}%) :`}
        valeur={fmt(resultat.cotisations_sociales)}
        muted
        indent
      />
      <LigneCalcul label="= REVENU NET avant impôt :" valeur={fmt(resultat.remuneration_nette_avant_pas)} strong dividerTop highlight />

      <Sep color="border-purple-300" />
      <BlocResultat label="Revenu net avant impôt" valeur={resultat.remuneration_nette_avant_pas} color="text-purple-600" />

      <div className="mt-4 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center gap-2">
        <span className="text-base">⚠️</span>
        <p className="text-xs font-semibold text-yellow-800">Protection sociale limitée</p>
      </div>
    </StatusCard>
  );
}

const chargesShape = PropTypes.shape({
  matieres_et_sous_traitance: PropTypes.number,
  frais_participation_coop: PropTypes.number,
  frais_fonctionnement: PropTypes.number,
  autres_frais_structure: PropTypes.number,
});

CarteCAE.propTypes = {
  resultat: PropTypes.shape({
    categories_charges: chargesShape,
    marge_brute: PropTypes.number,
    marge_economique: PropTypes.number,
    charges_patronales: PropTypes.number,
    salaire_brut_api: PropTypes.number,
    cotisations_salariales: PropTypes.number,
    salaire_net_avant_pas: PropTypes.number,
    frais_pro_recuperes: PropTypes.number,
    montant_total_verse: PropTypes.number,
    avertissement: PropTypes.string,
    erreur_config: PropTypes.string,
  }),
  metier: PropTypes.string.isRequired,
  caHT: PropTypes.number.isRequired,
};

CarteMicro.propTypes = {
  resultat: PropTypes.shape({
    incompatible: PropTypes.bool,
    incompatibilite: PropTypes.shape({
      ca_annuel: PropTypes.number,
      seuil: PropTypes.number,
      depassement: PropTypes.number,
    }),
    categories_charges: chargesShape,
    taux_cotisations: PropTypes.number,
    cotisations_sociales: PropTypes.number,
    revenu_net_avant_pas: PropTypes.number,
    charges_non_deductibles: PropTypes.number,
    revenu_reel_disponible: PropTypes.number,
    avertissement: PropTypes.string,
  }),
  metier: PropTypes.string.isRequired,
  caTTC: PropTypes.number.isRequired,
};

CarteSASU.propTypes = {
  resultat: PropTypes.shape({
    categories_charges: chargesShape,
    marge_brute: PropTypes.number,
    marge_economique: PropTypes.number,
    resultat_disponible: PropTypes.number,
    charges_patronales: PropTypes.number,
    salaire_brut: PropTypes.number,
    cotisations_salariales: PropTypes.number,
    salaire_net_avant_pas: PropTypes.number,
    avertissement: PropTypes.string,
  }),
  metier: PropTypes.string.isRequired,
  caHT: PropTypes.number.isRequired,
};

CarteEI.propTypes = {
  resultat: PropTypes.shape({
    categories_charges: chargesShape,
    marge_brute: PropTypes.number,
    marge_economique: PropTypes.number,
    benefice: PropTypes.number,
    taux_cotisations: PropTypes.number,
    cotisations_sociales: PropTypes.number,
    remuneration_nette_avant_pas: PropTypes.number,
  }),
  metier: PropTypes.string.isRequired,
  caHT: PropTypes.number.isRequired,
};
